import { useEffect, useState } from "react";
import { authFetch } from "../lib/api";
import {
  Archive,
  BookOpen,
  Edit3,
  Loader2,
  Minus,
  Plus,
  Search,
  Trash2,
  TrendingDown,
  TrendingUp,
  X,
} from "lucide-react";

const TREND_ICON = {
  up: TrendingUp,
  down: TrendingDown,
  flat: Minus,
};
const TREND_COLOR = {
  up: "text-green-600",
  down: "text-red-500",
  flat: "text-slate-400",
};
const TOPIC_EMPTY_FORM = {
  title: "",
  summary: "",
  hero_stat: "",
  hero_trend: "flat",
  talking_points: "",
  candidates: [],
  facts: [],
  sources: [],
  hot: false,
  sort_order: "0",
};

export const TopicsManagement = ({ facility }) => {
  const fid = facility?.facility_id;
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [formOpen, setFormOpen] = useState(false);
  const [editTopic, setEditTopic] = useState(null);
  const [form, setForm] = useState(TOPIC_EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(null);

  useEffect(() => {
    if (!fid) return;
    setLoading(true);
    authFetch(`/facility-topics?facility_id=${encodeURIComponent(fid)}`)
      .then((data) => {
        if (Array.isArray(data)) setTopics(data);
      })
      .finally(() => setLoading(false));
  }, [fid]);

  const openCreate = () => {
    setEditTopic(null);
    setForm(TOPIC_EMPTY_FORM);
    setFormOpen(true);
  };

  const openEdit = (t) => {
    setEditTopic(t);
    setForm({
      title: t.title || "",
      summary: t.summary || "",
      hero_stat: t.hero_stat || "",
      hero_trend: t.hero_trend || "flat",
      talking_points: Array.isArray(t.talking_points)
        ? t.talking_points.join("\n")
        : t.talking_points || "",
      candidates: Array.isArray(t.candidates)
        ? t.candidates.map((c) => ({
            name: c.name || "",
            position: c.position || "",
          }))
        : [],
      facts: Array.isArray(t.facts)
        ? t.facts.map((f) => ({ value: f.value || "", label: f.label || "" }))
        : [],
      sources: Array.isArray(t.sources)
        ? t.sources.map((s) => ({ name: s.name || "" }))
        : [],
      hot: !!t.hot,
      sort_order: String(t.sort_order ?? 0),
    });
    setFormOpen(true);
  };

  const setField = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const handleSave = async () => {
    if (!form.title.trim() || saving) return;
    setSaving(true);
    const parsedTalkingPoints = form.talking_points
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);
    const payload = {
      facility_id: fid,
      title: form.title.trim(),
      summary: form.summary.trim(),
      hero_stat: form.hero_stat.trim(),
      hero_trend: form.hero_trend,
      talking_points: form.talking_points,
      candidates: form.candidates.filter((c) => c.name.trim()),
      facts: form.facts.filter((f) => f.value.trim()),
      sources: form.sources.filter((s) => s.name.trim()),
      hot: form.hot,
      sort_order: parseInt(form.sort_order, 10) || 0,
    };
    if (editTopic) {
      const res = await authFetch(`/facility-topics/${editTopic.id}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      });
      if (!res.error) {
        setTopics((prev) =>
          prev.map((t) =>
            t.id === editTopic.id
              ? {
                  ...t,
                  title: payload.title,
                  summary: payload.summary,
                  hero_stat: payload.hero_stat,
                  hero_trend: payload.hero_trend,
                  hot: payload.hot,
                  sort_order: payload.sort_order,
                  talking_points: parsedTalkingPoints,
                  candidates: payload.candidates,
                  facts: payload.facts,
                  sources: payload.sources,
                }
              : t,
          ),
        );
        setFormOpen(false);
      }
    } else {
      const res = await authFetch("/facility-topics", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      if (res.id) {
        const newTopic = {
          id: res.id,
          slug: res.slug || "",
          title: payload.title,
          summary: payload.summary,
          hero_stat: payload.hero_stat,
          hero_trend: payload.hero_trend,
          hot: payload.hot,
          sort_order: payload.sort_order,
          scope: "facility",
          status: "active",
          talking_points: parsedTalkingPoints,
          candidates: payload.candidates,
          facts: payload.facts,
          sources: payload.sources,
          updated_at: new Date().toISOString().slice(0, 10),
        };
        setTopics((prev) => [newTopic, ...prev]);
        setFormOpen(false);
      }
    }
    setSaving(false);
  };

  const handleToggleStatus = async (topic) => {
    const nextStatus = topic.status === "active" ? "archived" : "active";
    setToggling(topic.id);
    const res = await authFetch(`/facility-topics/${topic.id}`, {
      method: "PATCH",
      body: JSON.stringify({ facility_id: fid, status: nextStatus }),
    });
    if (!res.error) {
      setTopics((prev) =>
        prev.map((t) => (t.id === topic.id ? { ...t, status: nextStatus } : t)),
      );
    }
    setToggling(null);
  };

  const handleDelete = async (topic) => {
    if (topic.scope === "global") {
      alert(
        "Global topics cannot be deleted — use Archive to hide them instead.",
      );
      return;
    }
    if (!window.confirm(`Delete "${topic.title}"?`)) return;
    setToggling(topic.id);
    await authFetch(
      `/facility-topics/${topic.id}?facility_id=${encodeURIComponent(fid)}`,
      { method: "DELETE" },
    );
    setTopics((prev) => prev.filter((t) => t.id !== topic.id));
    setToggling(null);
  };

  const filtered = topics.filter((t) => {
    if (statusFilter !== "all" && t.status !== statusFilter) return false;
    const q = search.toLowerCase();
    return (
      !q ||
      t.title.toLowerCase().includes(q) ||
      (t.summary || "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-slate-800">
            Issue Topics
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            {topics.filter((t) => t.status === "active").length} active ·{" "}
            {topics.filter((t) => t.scope === "facility").length} custom
          </p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" /> New Topic
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-3.5 h-3.5" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search topics…"
            className="w-full pl-8 pr-3 py-2 border border-slate-200 rounded-lg text-sm placeholder-slate-400 focus:border-blue-400 focus:outline-none"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-700 focus:border-blue-400 focus:outline-none bg-white"
        >
          <option value="active">Active</option>
          <option value="archived">Archived</option>
          <option value="all">All</option>
        </select>
      </div>

      {/* Topic list */}
      {loading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="animate-spin text-slate-400 w-6 h-6" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center text-slate-400 text-sm py-10">
          {topics.length === 0
            ? "No topics yet — create one or topics will inherit from global."
            : "No topics match your filters."}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((t) => {
            const TrendIcon = TREND_ICON[t.hero_trend] ?? Minus;
            const trendColor = TREND_COLOR[t.hero_trend] ?? "text-slate-400";
            const isGlobal = t.scope === "global";
            const isArchived = t.status === "archived";
            return (
              <div
                key={t.id}
                className={`bg-white border rounded-xl p-4 transition-colors ${
                  isArchived
                    ? "border-slate-100 opacity-60"
                    : "border-slate-200 hover:border-slate-300"
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    {/* Title row */}
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <BookOpen className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                      <span className="font-semibold text-slate-800 text-sm">
                        {t.title}
                      </span>
                      {t.hot && (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">
                          Hot
                        </span>
                      )}
                      <span
                        className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                          isGlobal
                            ? "bg-purple-100 text-purple-700"
                            : "bg-teal-100 text-teal-700"
                        }`}
                      >
                        {isGlobal ? "Global" : "Custom"}
                      </span>
                      {isArchived && (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">
                          Archived
                        </span>
                      )}
                    </div>

                    {/* Summary */}
                    {t.summary && (
                      <p className="text-xs text-slate-500 mb-2 leading-relaxed">
                        {t.summary}
                      </p>
                    )}

                    {/* Hero stat */}
                    {t.hero_stat && (
                      <div className="flex items-center gap-1.5 mb-2">
                        <TrendIcon className={`w-3.5 h-3.5 ${trendColor}`} />
                        <span className="text-xs font-medium text-slate-700">
                          {t.hero_stat}
                        </span>
                      </div>
                    )}

                    {/* Talking points */}
                    {Array.isArray(t.talking_points) &&
                      t.talking_points.length > 0 && (
                        <ul className="space-y-0.5">
                          {t.talking_points.slice(0, 3).map((pt, i) => (
                            <li
                              key={i}
                              className="flex items-start gap-1.5 text-[11px] text-slate-600"
                            >
                              <span className="text-blue-400 mt-0.5 flex-shrink-0">
                                ·
                              </span>
                              {pt}
                            </li>
                          ))}
                          {t.talking_points.length > 3 && (
                            <li className="text-[11px] text-slate-400 pl-3">
                              +{t.talking_points.length - 3} more
                            </li>
                          )}
                        </ul>
                      )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => openEdit(t)}
                      title={isGlobal ? "Edit global topic" : "Edit"}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                    >
                      <Edit3 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleToggleStatus(t)}
                      disabled={toggling === t.id}
                      title={isArchived ? "Restore" : "Archive"}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-amber-600 hover:bg-amber-50 transition-colors disabled:opacity-40"
                    >
                      {toggling === t.id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Archive className="w-3.5 h-3.5" />
                      )}
                    </button>
                    {!isGlobal ? (
                      <button
                        onClick={() => handleDelete(t)}
                        disabled={toggling === t.id}
                        title="Delete"
                        className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-40"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    ) : (
                      <button
                        disabled
                        title="Global topics can't be deleted — archive to hide"
                        className="p-1.5 rounded-lg text-slate-200 cursor-not-allowed"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create / Edit modal */}
      {formOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={() => !saving && setFormOpen(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4 p-6 space-y-4 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-slate-800">
                {editTopic ? "Edit Topic" : "New Topic"}
              </h3>
              <button
                onClick={() => setFormOpen(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {editTopic?.scope === "global" && (
              <div className="flex items-start gap-2 px-3 py-2 bg-purple-50 border border-purple-200 rounded-lg text-xs text-purple-700">
                <span className="font-semibold mt-0.5">Global topic.</span>
                <span>
                  Changes apply across all facilities that inherit this topic.
                </span>
              </div>
            )}

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  value={form.title}
                  onChange={(e) => setField("title", e.target.value)}
                  placeholder="Border Security"
                  autoFocus
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-blue-400 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">
                  Summary
                </label>
                <textarea
                  value={form.summary}
                  onChange={(e) => setField("summary", e.target.value)}
                  placeholder="One-paragraph description shown to canvassers"
                  rows={2}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-blue-400 focus:outline-none resize-none"
                />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <label className="block text-xs font-semibold text-slate-600 mb-1">
                    Hero Stat
                  </label>
                  <input
                    value={form.hero_stat}
                    onChange={(e) => setField("hero_stat", e.target.value)}
                    placeholder="78% of AZ voters support..."
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-blue-400 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">
                    Trend
                  </label>
                  <select
                    value={form.hero_trend}
                    onChange={(e) => setField("hero_trend", e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-blue-400 focus:outline-none bg-white"
                  >
                    <option value="up">↑ Up</option>
                    <option value="down">↓ Down</option>
                    <option value="flat">— Flat</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">
                  Talking Points{" "}
                  <span className="font-normal text-slate-400">
                    (one per line)
                  </span>
                </label>
                <textarea
                  value={form.talking_points}
                  onChange={(e) => setField("talking_points", e.target.value)}
                  placeholder={
                    "Support completing physical barrier\nOppose catch-and-release\nBack local law enforcement cooperation"
                  }
                  rows={5}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-blue-400 focus:outline-none resize-none font-mono"
                />
              </div>

              {/* Candidate Positions */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-semibold text-slate-600">
                    Candidate Positions
                  </label>
                  <button
                    type="button"
                    onClick={() =>
                      setField("candidates", [
                        ...form.candidates,
                        { name: "", position: "" },
                      ])
                    }
                    className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                  >
                    + Add
                  </button>
                </div>
                {form.candidates.length === 0 ? (
                  <p className="text-xs text-slate-400">None added.</p>
                ) : (
                  <div className="space-y-2">
                    {form.candidates.map((c, i) => (
                      <div key={i} className="flex gap-2 items-center">
                        <input
                          value={c.name}
                          onChange={(e) => {
                            const a = [...form.candidates];
                            a[i] = { ...a[i], name: e.target.value };
                            setField("candidates", a);
                          }}
                          placeholder="Name"
                          className="w-1/3 px-2.5 py-1.5 border border-slate-200 rounded-lg text-sm focus:border-blue-400 focus:outline-none"
                        />
                        <input
                          value={c.position}
                          onChange={(e) => {
                            const a = [...form.candidates];
                            a[i] = { ...a[i], position: e.target.value };
                            setField("candidates", a);
                          }}
                          placeholder="Position statement"
                          className="flex-1 px-2.5 py-1.5 border border-slate-200 rounded-lg text-sm focus:border-blue-400 focus:outline-none"
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setField(
                              "candidates",
                              form.candidates.filter((_, j) => j !== i),
                            )
                          }
                          className="p-1 text-slate-400 hover:text-red-500 flex-shrink-0 text-base leading-none"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Key Facts */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-semibold text-slate-600">
                    Key Facts
                  </label>
                  <button
                    type="button"
                    onClick={() =>
                      setField("facts", [
                        ...form.facts,
                        { value: "", label: "" },
                      ])
                    }
                    className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                  >
                    + Add
                  </button>
                </div>
                {form.facts.length === 0 ? (
                  <p className="text-xs text-slate-400">None added.</p>
                ) : (
                  <div className="space-y-2">
                    {form.facts.map((f, i) => (
                      <div key={i} className="flex gap-2 items-center">
                        <input
                          value={f.value}
                          onChange={(e) => {
                            const a = [...form.facts];
                            a[i] = { ...a[i], value: e.target.value };
                            setField("facts", a);
                          }}
                          placeholder="Value (e.g. 78%)"
                          className="w-1/3 px-2.5 py-1.5 border border-slate-200 rounded-lg text-sm focus:border-blue-400 focus:outline-none"
                        />
                        <input
                          value={f.label}
                          onChange={(e) => {
                            const a = [...form.facts];
                            a[i] = { ...a[i], label: e.target.value };
                            setField("facts", a);
                          }}
                          placeholder="Label (e.g. Voter Support)"
                          className="flex-1 px-2.5 py-1.5 border border-slate-200 rounded-lg text-sm focus:border-blue-400 focus:outline-none"
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setField(
                              "facts",
                              form.facts.filter((_, j) => j !== i),
                            )
                          }
                          className="p-1 text-slate-400 hover:text-red-500 flex-shrink-0 text-base leading-none"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Sources */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-semibold text-slate-600">
                    Sources
                  </label>
                  <button
                    type="button"
                    onClick={() =>
                      setField("sources", [...form.sources, { name: "" }])
                    }
                    className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                  >
                    + Add
                  </button>
                </div>
                {form.sources.length === 0 ? (
                  <p className="text-xs text-slate-400">None added.</p>
                ) : (
                  <div className="space-y-2">
                    {form.sources.map((s, i) => (
                      <div key={i} className="flex gap-2 items-center">
                        <input
                          value={s.name}
                          onChange={(e) => {
                            const a = [...form.sources];
                            a[i] = { ...a[i], name: e.target.value };
                            setField("sources", a);
                          }}
                          placeholder="Source name (e.g. AZ Dept. of Education)"
                          className="flex-1 px-2.5 py-1.5 border border-slate-200 rounded-lg text-sm focus:border-blue-400 focus:outline-none"
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setField(
                              "sources",
                              form.sources.filter((_, j) => j !== i),
                            )
                          }
                          className="p-1 text-slate-400 hover:text-red-500 flex-shrink-0 text-base leading-none"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={form.hot}
                    onChange={(e) => setField("hot", e.target.checked)}
                    className="w-4 h-4 accent-orange-500"
                  />
                  <span className="text-sm text-slate-700 font-medium">
                    Mark as Hot
                  </span>
                </label>
                <div className="flex items-center gap-2">
                  <label className="text-xs font-semibold text-slate-600">
                    Sort Order
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={form.sort_order}
                    onChange={(e) => setField("sort_order", e.target.value)}
                    className="w-16 px-2 py-1.5 border border-slate-200 rounded-lg text-sm focus:border-blue-400 focus:outline-none text-center"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-1">
              <button
                onClick={() => setFormOpen(false)}
                className="flex-1 py-2.5 border border-slate-200 rounded-lg text-slate-600 text-sm font-medium hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={!form.title.trim() || saving}
                className="flex-1 py-2.5 bg-blue-600 rounded-lg text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-40 transition-colors flex items-center justify-center gap-2"
              >
                {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                {saving
                  ? "Saving…"
                  : editTopic
                    ? "Save Changes"
                    : "Create Topic"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Walk Path Management ────────────────────────────────────────────
